import ReactGA from "react-ga4";

const GA_MEASUREMENT_ID = "G-GJ6648WNCV";

export const initGA = () => {
  ReactGA.initialize(GA_MEASUREMENT_ID);
};

export const trackPageView = (path) => {
  ReactGA.send({ hitType: "pageview", page: path });
};

export const trackEvent = (action, category, label, value) => {
  ReactGA.event({ action, category, label, value });
};

// Custom event helpers matching the user's requirements
export const trackCTAClick = (ctaName, location) => {
  ReactGA.event({
    action: "cta_click",
    category: "engagement",
    label: `${ctaName} - ${location}`
  });
};

export const trackFormSubmit = (formName, solution) => {
  const language = localStorage.getItem("documatch_language") || "fr";
  // Legacy event (kept for backward-compatible analytics dashboards)
  ReactGA.event({
    action: "form_submit",
    category: "conversion",
    label: `${formName}${solution ? ` - ${solution}` : ""}`
  });
  // GA4 CONVERSION event (via direct gtag to preserve exact lowercase snake_case)
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "lead_form_submitted", {
      event_category: "conversion",
      event_label: `${formName}${solution ? "_" + solution : ""}_${language}`
    });
  }
};

export const trackLandingView = (solutionId) => {
  ReactGA.event({
    action: "landing_view",
    category: "engagement",
    label: solutionId
  });
};

export const trackPopupRedirect = (solutionId) => {
  ReactGA.event({
    action: "popup_redirect",
    category: "engagement",
    label: solutionId
  });
};

export const trackEbookDownload = (source) => {
  ReactGA.event({
    action: "ebook_download",
    category: "conversion",
    label: source
  });
  // Also track internally
  const API = process.env.REACT_APP_BACKEND_URL;
  fetch(`${API}/api/track-event`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ event: "ebook_download", source, language: localStorage.getItem("documatch_language") || "fr" })
  }).catch(() => {});
};

export const trackInternalEvent = (event, data = {}) => {
  const API = process.env.REACT_APP_BACKEND_URL;
  fetch(`${API}/api/track-event`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ event, ...data, language: localStorage.getItem("documatch_language") || "fr" })
  }).catch(() => {});
};

// ==============================================================
// Direct gtag() wrapper — preserves EXACT lowercase snake_case
// event names (react-ga4 title-cases the first letter which
// would break configured GA4 conversions like `analysis_completed`
// and `lead_form_submitted`).
// ==============================================================
const gtagEvent = (name, params = {}) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", name, params);
  }
};

// ==============================================================
// AI Analysis conversion funnel (GA4 events)
// Configure the following as CONVERSIONS in GA4 admin:
//   - lead_form_submitted
//   - analysis_completed
// ==============================================================

// Step 1 — CTA click "Commencer l'analyse" / "Iniciar análisis" / "Start AI analysis"
export const trackAnalysisCtaClick = (location = "hero") => {
  gtagEvent("cta_start_analysis_clicked", {
    event_category: "funnel",
    event_label: location
  });
};

// Step 2 — Analysis modal opened / first step rendered
export const trackAnalysisStarted = () => {
  gtagEvent("analysis_started", {
    event_category: "funnel",
    event_label: "analyzer_opened"
  });
};

// Step 3 — Progress by step (fires on each step change)
export const trackAnalysisStepProgress = (stepNumber, stepName) => {
  gtagEvent("analysis_step_progress", {
    event_category: "funnel",
    event_label: `step_${stepNumber}_${stepName || ""}`.replace(/_$/, ""),
    value: stepNumber
  });
};

// Step 4 — Analysis completed (GA4 CONVERSION)
export const trackAnalysisCompleted = (recommendationCount = 3, language = "fr") => {
  gtagEvent("analysis_completed", {
    event_category: "conversion",
    event_label: `results_${language}`,
    value: recommendationCount
  });
};

// Step 5 — Lead form submission (GA4 CONVERSION)
export const trackLeadFormSubmitted = (formName, solution = null, language = "fr") => {
  gtagEvent("lead_form_submitted", {
    event_category: "conversion",
    event_label: `${formName}${solution ? "_" + solution : ""}_${language}`
  });
};
